import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getStaffCommentSubjects } from '../../../api/staff-comment-subjects/get-staff-comment-subjects'
import { StaffCommentInput } from '../../../lib/types/models/staff-comment/staff-comment-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { createDateSchema } from '../../../lib/utils/validations/date.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import DateChip from '../../elements/date-chip'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<StaffCommentInput, true> & {
  className?: string
}

const formSchema: ZodType<StaffCommentInput> = z.object({
  id: createNumberSchema('id', { optional: true }),
  comment: createStringSchema('commentaire'),
  displayInPlanning: z.boolean(),
  displayForDirector: z.boolean(),
  subject: z.object({ id: createNumberSchema('id'), name: createStringSchema('sujet') }).nullish(),
  commentedByName: createStringSchema('NOM'),
  date: createDateSchema({ inputName: 'date' })
})

export function StaffCommentForm({ className, defaultValues, ...validationButtonProps }: Props) {
  const form = useForm<StaffCommentInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data } = useQuery({
    queryKey: ['staff-comment-subjects'],
    queryFn: () => getStaffCommentSubjects()
  })
  console.log('form ', form.getValues())
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <DateChip date={new Date()} label={'Date'} />

          <FormField
            control={form.control}
            name={'subject'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    options={data?.data.map((subject) => ({
                      label: subject.name,
                      value: subject.id
                    }))}
                    label={'Sujet'}
                    selectedOption={getSelectedOption(
                      field.value?.id,
                      data?.data.find((subject) => subject.id === field.value?.id)?.name
                    )}
                    onChange={(option) => {
                      const selectedSubject = data?.data.find((subject) => subject.id === option)
                      field.onChange({ id: selectedSubject?.id, name: selectedSubject?.name })
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'comment'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    label={'Votre commentaire...'}
                    type={'textarea'}
                    required
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Todo: create a visibility enum and map over the keys */}
          <div className={'flex flex-col gap-2'}>
            <Typography variant={'label-semibold'}>Visibilité : *</Typography>
            <div className={'flex flex-col gap-2'}>
              <FormField
                control={form.control}
                name={'displayForDirector'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <div className={'flex flex-row items-center gap-7'}>
                        <ControlledRadioInput
                          {...field}
                          label={'Indéfiniment'}
                          checked={field.value === true}
                          onCheck={() => field.onChange(true)}
                        />
                        <ControlledRadioInput
                          {...field}
                          label={'Année en cours'}
                          checked={field.value === false}
                          onCheck={() => field.onChange(false)}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={'flex flex-col gap-2'}>
            <Typography variant={'label-semibold'}>Planning : *</Typography>
            <div className={'flex flex-col gap-2'}>
              <FormField
                control={form.control}
                name={'displayInPlanning'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <div className={'flex flex-row items-center gap-7'}>
                        <ControlledRadioInput
                          {...field}
                          label={'Oui'}
                          checked={field.value === true}
                          onCheck={() => field.onChange(true)}
                        />
                        <ControlledRadioInput
                          {...field}
                          label={'Non'}
                          checked={field.value === false}
                          onCheck={() => field.onChange(false)}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
